import { Tabs, type TabsProps } from '@mantine/core';
import { useState } from 'react';

import type { NavLink } from '../constants';

export interface FloatingTabsProps<T extends string> extends Omit<TabsProps, 'children'> {
  links: NavLink<T>[];
  renderIndicator: (parent: HTMLElement | null, target: HTMLElement | null) => React.ReactNode;
  renderTab: (
    link: NavLink<T>,
    setControlRef: (val: T) => (node: HTMLButtonElement) => void,
  ) => React.ReactNode;
}

export function FloatingTabs<T extends string>({
  classNames,
  links,
  onChange,
  renderIndicator,
  renderTab,
  value,
  variant,
  visibleFrom,
}: FloatingTabsProps<T>) {
  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});
  const setControlRef = (val: T) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  return (
    <Tabs
      classNames={classNames}
      onChange={onChange}
      value={value}
      variant={variant}
      visibleFrom={visibleFrom}>
      <Tabs.List ref={setRootRef}>
        {links.map((link) => renderTab(link, setControlRef))}
        {renderIndicator(rootRef, value ? controlsRefs[value] : null)}
      </Tabs.List>
    </Tabs>
  );
}
